import { NavigateBefore } from '@mui/icons-material';
import { ReactComponent as MedicationIcon } from 'assets/Medication.svg';
import { Box, ButtonBase, Divider, Typography } from '@mui/material';
import Tabs from 'pages/Dashboard/components/Tabs';
import { ShowMoreDrawerChildProps, sliceForShowMore } from 'pages/Dashboard/ShowMoreDrawer';
import { MedicationCategory, PatientMedication } from 'pages/Dashboard/types/medication.types';
import React, { useEffect, useState } from 'react';
import theme from 'theme';
import { googleDateToDateString } from 'utils/dateUtils';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import UserAddedToolTip from 'pages/Dashboard/components/UserAddedToolTip';
// eslint-disable-next-line import/no-cycle
import { getMedicationName } from 'pages/Dashboard/HealthRecordsNew';
import NoHealthRecordDataCard, { HealthRecordCategory } from 'components/NoHealthRecordDataCard';
import PatientDetailsCard from 'components/PatientDetailsCard';
import HLCard from 'components/HLCard';

export enum TabLabels {
  PSYCH = 'PSYCH',
  OTHER = 'OTHER',
}

export interface MedicationCardProps extends Partial<ShowMoreDrawerChildProps> {
  gridSize?: number;
  medication?: PatientMedication[];
  showTabs: boolean;
  drawerTabIndex?: number;
  onShowMoreMedication: (d: PatientMedication[], idx?: number) => void;
  toggleDrawer: () => void;
  setSelectedMedication: (medication: PatientMedication) => void;
  setIsOpenedFromChart: (isOpened: boolean) => void;
}

type TabPanelsProps = {
  children?: React.ReactNode;
  medication: PatientMedication[];
  handleSelectedMedication: (index: number) => void;
};
const getMedicationDate = (medication: PatientMedication) => {
  if (!medication.started) {
    return '';
  }
  return new Date(
    medication.started.year,
    medication.started.month - 1,
    medication.started.day,
  ).getTime();
};
function addSuperscriptR(str: string) {
  return str.replace(/\(([^)]+)\)$/, '($1®)');
}
function MedicationTabPanels({
  children,
  medication,
  handleSelectedMedication,
}: TabPanelsProps): JSX.Element {
  const unknownDateMedications = medication.filter((med) => !med.started);
  return (
    <>
      {medication.map((currentMedication, index) => {
        if (!currentMedication.started) {
          return null;
        }
        const isSameDate = googleDateToDateString(currentMedication.started)
          === googleDateToDateString(currentMedication.stopped);
        const dateString = isSameDate
          ? googleDateToDateString(currentMedication.started)
          : `${googleDateToDateString(currentMedication.started)}${
            currentMedication.stopped
              ? ` - ${googleDateToDateString(currentMedication.stopped)}`
              : ''
          }`;
        return (
          <>
            <ButtonBase
              key={`current-regimen-${currentMedication.id}`}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
              onClick={() => handleSelectedMedication(index)}
            >
              {/* Left side: Icon + Name + Date/Dosage */}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <MedicationIcon
                    style={{
                      width: 16,
                      height: 16,
                      marginRight: 8,
                    }}
                  />
                  <Typography
                    variant='body1'
                    color='text.primary'
                    textAlign='left'
                    dangerouslySetInnerHTML={{
                      __html: addSuperscriptR(getMedicationName(currentMedication)?.trim()).replace(
                        /®/g,
                        '<sup>®</sup>',
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                  >
                    {dateString}
                  </Typography>
                  {dateString && currentMedication?.medication?.strength && (
                  <Box
                    sx={{
                      mx: 1,
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: theme.custom.colors.lightTextSecondary,
                    }}
                  />
                  )}
                  <Typography
                    variant='caption'
                    color='text.secondary'
                  >
                    {currentMedication?.medication?.strength}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UserAddedToolTip source={currentMedication.source} />
                {currentMedication.refillCount > 1 && (
                  <Box
                    sx={{
                      background: theme.custom.colors.backgroundColorSecondary,
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      color={theme.custom.colors.lightTextSecondary}
                      fontSize={14}
                      fontWeight={500}
                    >
                      {currentMedication.refillCount}
                    </Typography>
                  </Box>
                )}
                <NavigateBefore
                  sx={{
                    color: theme.custom.colors.lightTextSecondary,
                    fontSize: 28,
                    transform: 'rotate(180deg)',
                  }}
                />
              </Box>
            </ButtonBase>
            {index < medication.length - 1 && (
              <Divider
                sx={{ borderColor: theme.custom.colors.borderColor, width: '100%', my: 1 }}
              />
            )}
          </>
        );
      })}
      {unknownDateMedications.length ? (
        <>
          <Typography mb={2} mt={3} variant='body1' color='text.secondary'>
            Unknown dates
          </Typography>
          {unknownDateMedications.map((meds, index) => (
            <Box>
              <Typography
                display='inline-block'
                dangerouslySetInnerHTML={{
                  __html: addSuperscriptR(getMedicationName(meds)).replace(/®/g, '<sup>®</sup>'),
                }}
              />
              <Typography variant='subtitle1' color={theme.custom.colors.lightTextSecondary}>
                {`${meds?.medication?.strength}`}
              </Typography>
              {index < unknownDateMedications.length - 1 && (
                <Divider
                  sx={{ borderColor: theme.custom.colors.borderColor, width: '100%', my: 1 }}
                />
              )}
            </Box>
          ))}
        </>
      ) : null}
      {children}
    </>
  );
}

export default function MedicationCardNew({
  gridSize,
  medication = [],
  showTabs,
  drawerTabIndex,
  drawerMode = false,
  toggleDrawer = () => null,
  onShowMoreMedication = () => null,
  setSelectedMedication,
  setIsOpenedFromChart,
}: MedicationCardProps): JSX.Element | null {
  const [value, setValue] = React.useState(0);
  const [finalMedsList, setFinalMedsList] = useState<PatientMedication[]>([]);
  const [psychMedication, setPsychMedication] = useState<PatientMedication[]>([]);
  const [otherMedication, setOtherMedication] = useState<PatientMedication[]>([]);

  useEffect(() => {
    const datedMedications = medication
      .filter((meds) => meds.started)
      .sort((a, b) => (getMedicationDate(a) > getMedicationDate(b) ? -1 : 1));
    const nonDatedMedications = medication.filter((meds) => !meds.started);

    const sortedMedications = [...datedMedications, ...nonDatedMedications];
    setFinalMedsList(sortedMedications);
  }, [medication]);
  useEffect(() => {
    const psych = finalMedsList.filter(
      (med) => med.category === MedicationCategory.MEDICATION_CATEGORY_PSYCH,
    );
    const other = finalMedsList.filter(
      (med) => med.category !== MedicationCategory.MEDICATION_CATEGORY_PSYCH,
    );
    setPsychMedication(psych);
    setOtherMedication(other);
  }, [finalMedsList]);

  const [currentMedsList, setCurrentMedsList] = useState<PatientMedication[]>([]);
  const slicedCurrentMedsList = sliceForShowMore<PatientMedication>(
    currentMedsList,
    !drawerMode,
    6,
  );
  React.useEffect(() => {
    if (drawerMode) {
      setValue(drawerTabIndex || 0);
    }
  }, [drawerTabIndex, drawerMode]);
  const handleSelectedMedicationList = (tab: number) => {
    setValue(tab);
  };
  useEffect(() => {
    setCurrentMedsList(value === 0 ? psychMedication : otherMedication);
  }, [value, psychMedication, otherMedication]);
  useEffect(() => {
    if (!psychMedication.length && otherMedication.length) {
      setValue(1);
    }
  }, [psychMedication, otherMedication]);
  const handleShowMoreMedication = (medicine: PatientMedication[], index?: number) => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_SHOW_MORE_MEDICATION_CLICKED, {
      location: 'medical_history_tab_medication_show_more',
    });
    onShowMoreMedication(medicine, index);
  };

  const renderTabs = () => (showTabs ? (
    <Box display='flex' flexDirection='column' flexGrow={1}>
      <Box mb={2} sx={{ boxSizing: 'border-box' }}>
        <Tabs
          tabItems={[
            { label: `${TabLabels.PSYCH} (${psychMedication.length})`, value: 0 },
            { label: `${TabLabels.OTHER} (${otherMedication.length})`, value: 1 },
          ]}
          selectedTab={value}
          setSelectedTab={(tab) => handleSelectedMedicationList(tab)}
        />
      </Box>
      <Box display='flex' flexDirection='column' height='100%'>
        <HLCard sx={{ flexGrow: 1, p: drawerMode ? 1.5 : 0, border: 'none' }}>
          <MedicationTabPanels
            medication={slicedCurrentMedsList}
            handleSelectedMedication={handleSelectedMedication}
          />
        </HLCard>
      </Box>
    </Box>
  ) : (
    <HLCard sx={{ p: drawerMode ? 1.5 : 0, border: 'none' }}>
      <MedicationTabPanels
        medication={currentMedsList}
        handleSelectedMedication={handleSelectedMedication}
      />
    </HLCard>
  ));

  const handleSelectedMedication = (index: number) => {
    setSelectedMedication(currentMedsList?.[index]);
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_MEDICATION_NAME_CLICKED, {
      medicationName: currentMedsList?.[index]?.medication?.brandName ?? '',
      location: 'medical_history_tab_medication_name',
    });
    setIsOpenedFromChart(false);
    toggleDrawer();
  };

  if (drawerMode) {
    return renderTabs();
  }

  return (
    <PatientDetailsCard
      title='Medications'
      showHeader={!drawerMode && medication.length > 0}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      viewDetailsBtnLabel='View all'
      showViewDetails={!drawerMode && currentMedsList.length > 6 && !!handleShowMoreMedication}
      onViewDetails={() => handleShowMoreMedication(medication, value)}
      key='current-regimen-medication'
      showSubHeader={false}
    >
      {!medication.length ? (
        <NoHealthRecordDataCard
          title='No medication found'
          description='Data will be available here once there is a activity to record'
          healthRecordCategory={HealthRecordCategory.medications}
        />
      ) : (
        <>{renderTabs()}</>
      )}
    </PatientDetailsCard>
  );
}
